<template>
	<div v-if="cmsGoals.length" class="card-page goals-setting-wrapper">
		<ja-header
			:title="$t('gamePlayMetaTitles.goals')"
			subtitle="JA Finance Park"
		>
			<template v-slot:icon>
				<svgicon name="headerIcons/goals"></svgicon>
			</template>
		</ja-header>
		<content>
			<div class="building-wrapper">
				<ja-building-header
					:asset="'page-headers/goals-building'"
					:billboard-asset="localSponsor"
					billboard-position="right"
					type="page-header"
				></ja-building-header>
			</div>
			<h1 class="heading">{{ $t('goalsScreenTitleQuestion') }}</h1>
			<h2 class="body-text-1 sub-heading">
				{{ $t('goalsScreenTitleInfo') }}
			</h2>
			<ja-card
				v-for="(goal, i) in cmsGoals"
				:key="i"
				:selected="goalIsSelected(goal)"
				role="switch"
				:aria-checked="goalIsSelected(goal).toString()"
				:title="goal.name"
				:aria-label="`${goal.name}. ${goal.description}`"
				@click.native="toggleGoalSelection(goal)"
			>
				<span class="card-description" aria-hidden="true">{{
					goal.description
				}}</span>
				<ja-multiselect-circle
					tabindex="-1"
					:selected="goalIsSelected(goal)"
					@selected="toggleGoalSelection(goal)"
				></ja-multiselect-circle>
			</ja-card>
		</content>

		<ja-alert-bar :alert-condition="alertIsVisible">
			{{ $t('goalsCountAlert') }}
		</ja-alert-bar>

		<footer class="sticky-footer">
			<div class="footer-content">
				<span class="counter">{{
					$t('selectedGoalsCounter', { count: selectedGoalsCount })
				}}</span>
				<ja-button
					:disabled="nextButtonIsDisabled"
					class="small right"
					name="next-goal"
					:aria-label="$t('goToSimulation')"
					@click.native.once="submitGoals"
				>
					<span class="button-text">{{ $t('finish') }}</span>
					<svgicon name="arrow-right" class="arrow-right"></svgicon>
				</ja-button>
			</div>
		</footer>
	</div>
</template>

<script>
import '../components/icons/headerIcons/goals';
import gql from 'graphql-tag';
import UserData from '../mixins/UserData';
import Accessibility from '../mixins/Accessibility';
import Button from '../components/ui/Button';
import Header from '../components/ui/headers/Header';
import MultiselectCircle from '../components/ui/MultiselectCircle';
import Card from '../components/ui/Card';
import '../components/icons/arrow-right';
import AlertBar from '../components/ui/AlertBar';
import NavigationLoading from '../mixins/NavigationLoading';
import BuildingHeader from '@/components/ui/headers/BuildingHeader';
import LocalSponsorService from '@/services/LocalSponsorService';

export default {
	components: {
		jaHeader: Header,
		jaButton: Button,
		jaMultiselectCircle: MultiselectCircle,
		jaCard: Card,
		jaAlertBar: AlertBar,
		jaBuildingHeader: BuildingHeader
	},
	mixins: [UserData, NavigationLoading, Accessibility],
	data() {
		return {
			cmsGoals: [],
			selectedGoals: [],
			alertIsVisible: false,
			addGoalsErrorMessage: null,
			localSponsor: null
		};
	},
	computed: {
		goalsCountIsExceeded() {
			return this.selectedGoalsCount >= this.MAX_SELECTABLE_GOALS;
		},
		selectedGoalsCount() {
			return this.selectedGoals.length;
		},
		nextButtonIsDisabled() {
			return this.selectedGoals.length < this.MIN_SELECTABLE_GOALS;
		},
		constants() {
			return this.$store.getters.constants;
		},
		MIN_SELECTABLE_GOALS() {
			if (this.constants) {
				return this.constants.minSelectableGoals;
			}
			return null;
		},
		MAX_SELECTABLE_GOALS() {
			if (this.constants) {
				return this.constants.maxSelectableGoals;
			}
			return null;
		}
	},
	mounted() {
		LocalSponsorService.getLogo('Goals', this.userData.studentFacilityId).then(
			logo => (this.localSponsor = logo)
		);
	},
	created() {
		if (this.userData) {
			this.$store.dispatch('getPauseState');
		}
	},
	apollo: {
		cmsGoals: {
			query: gql`
				{
					cmsGoals {
						goal_ID
						name
						description
					}
				}
			`,
			update(data) {
				this.$store.commit('setLoading', false);
				return data.cmsGoals;
			}
		}
	},
	methods: {
		toggleGoalSelection(goal) {
			if (this.goalIsSelected(goal)) {
				var selectedGoalIndex = this.selectedGoals.findIndex(
					x => x.name === goal.name
				);
				this.selectedGoals.splice(selectedGoalIndex, 1);

				if (this.alertIsVisible) {
					this.alertIsVisible = false;
				}
			} else {
				if (!this.goalsCountIsExceeded) {
					this.selectedGoals.push(goal);
				} else {
					setTimeout(() => {
						this.announceError(this.$t('goalsCountAlert'));
					}, 400);
					this.alertIsVisible = true;
				}
			}
		},
		goalIsSelected(goal) {
			return this.selectedGoals.some(x => x.name === goal.name);
		},
		async submitGoals() {
			this.$store.commit('setLoading', true);
			const list = [];
			var studentId = this.userData.studentId;

			this.selectedGoals.forEach(goal => {
				let answerObj = {};

				answerObj.studentId = studentId;
				answerObj.goal_Id = goal.goal_ID;
				list.push(answerObj);
			});

			list.push({ studentId: studentId, goal_Id: null });
			await this.$apollo
				.mutate({
					mutation: gql`
						mutation($list: [StudentGoalAddInputObjectGraphType]) {
							addManyStudentGoal(list: $list) {
								id
							}
						}
					`,
					variables: {
						list: list
					}
				})
				.then(response => {
					if (response.data) {
						this.$router.push('/home');
					}
				})
				.catch(error => {
					this.addGoalsErrorMessage = error;
					this.$store.commit('setLoading', false);
				});
		}
	}
};
</script>
<style lang="scss" scoped>
.goals-setting-wrapper {
	content {
		.building-wrapper {
			width: 768px;
			margin: auto;
		}

		.card {
			width: 66%;
			margin-left: auto;
			margin-right: auto;
			max-width: 848px;
			position: relative;

			@media (any-hover: hover) {
				&:hover {
					::v-deep * {
						color: #ffffff;
					}
					::v-deep .multi-select-circle {
						svg {
							fill: $icon-hover;
						}
					}
				}
			}

			.card-description {
				padding-right: 10%;
				display: inline-flex;
				min-width: 100%;
			}

			.multi-select-circle {
				top: 50%;
				position: absolute;
				float: right;
				-webkit-transform: translate(-50%, -50%);
				-moz-transform: translate(-50%, -50%);
				transform: translate(-50%, -50%);
			}
		}
	}
}
footer {
	justify-content: flex-end;
}
</style>
